import { useEffect } from "react";

import { css } from "@compiled/react";

import StrokeWeightExtraLargeIcon from "@atlaskit/icon/core/stroke-weight-extra-large";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import { useHeadingAtTop, toKebabCase } from "./utils";

export interface SideNavLinkProps {
    type: "link";
    text: string;
    href: string;
    anchors?: string[];
    depth?: number;
    expandParentCollection?: () => void;
    isAnchorLink?: boolean;
    isAnchorAtTop?: boolean;
}

const getCurrRoute = () => {
    let currRoute = window.location.pathname;
    if (currRoute === "/uniform-preview") {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const pathParam = urlParams.get("path");
        currRoute = pathParam ? decodeURIComponent(pathParam) : "";
    }
    return currRoute;
};

// eslint-disable-next-line complexity
export const SideNavLink = ({
    text,
    href,
    anchors,
    depth = 0,
    expandParentCollection,
    isAnchorLink = false,
    isAnchorAtTop = false
}: SideNavLinkProps) => {
    const localizedHref = useLocalizedHref(href);
    const { headingIdAtTop, validAnchorIds } = useHeadingAtTop(anchors);

    const currRoute = getCurrRoute();
    const isActive = currRoute === localizedHref;

    const shouldShowIcon =
        (isActive && !anchors && !isAnchorLink) || isAnchorAtTop;
    const shouldBold = (isActive && !isAnchorLink) || isAnchorAtTop;

    useEffect(() => {
        if (expandParentCollection && isActive) {
            expandParentCollection();
        }
    }, [expandParentCollection, isActive]);

    // Filter anchors to only include those with corresponding H2s in body-content
    const validAnchors =
        anchors && isActive
            ? anchors.filter((anchor: string) => {
                  const anchorId = toKebabCase(anchor);
                  return validAnchorIds.has(anchorId);
              })
            : [];

    return (
        <li
            css={[
                listStyleNoneCss,
                listItemCss,
                depth === 0 && css({ paddingBlock: token("space.200") })
            ]}
        >
            <a
                href={localizedHref}
                css={[
                    navLinkCss,
                    depth > 0 && subLevelLinkCss,
                    !isActive && !isAnchorAtTop && navLinkHoverCss
                ]}
            >
                <div css={dotContainerCss}>
                    {shouldShowIcon && <StrokeWeightExtraLargeIcon label="" />}
                </div>
                <span
                    className={depth === 0 || shouldBold ? "h6" : "body-md"}
                    style={{
                        fontFamily: wacToken("font.charlie.text")
                    }}
                >
                    {text}
                </span>
            </a>
            {validAnchors.length > 0 && (
                <ul
                    css={[
                        listStyleNoneCss,
                        css({
                            paddingInlineStart: token("space.400")
                        })
                    ]}
                >
                    {validAnchors.map((anchor: string) => {
                        const anchorId = toKebabCase(anchor);
                        const anchorIsAtTop = headingIdAtTop === anchorId;

                        return (
                            <SideNavLink
                                key={anchor}
                                type="link"
                                href={`${localizedHref}#${anchorId}`}
                                text={anchor}
                                depth={depth + 1}
                                isAnchorLink={true}
                                isAnchorAtTop={anchorIsAtTop}
                            />
                        );
                    })}
                </ul>
            )}
        </li>
    );
};

const listStyleNoneCss = css({
    listStyle: "none",
    margin: token("space.0"),
    padding: token("space.0")
});

const listItemCss = css({
    display: "block"
});

const navLinkCss = css({
    display: "flex",
    alignItems: "center"
});

const navLinkHoverCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:hover > span": {
        color: wacToken("color.primary.blue")
    }
});

const subLevelLinkCss = css({
    paddingBlock: token("space.100")
});

const dotContainerCss = css({
    width: "24px",
    height: "24px",
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    marginInlineEnd: token("space.050"),
    alignSelf: "flex-start"
});
